import { CredifyClass } from "./common";
import { validate as isValidUUID } from "uuid";
import {
  camelize,
  decamelize,
  isObjectNullOrEmpty,
  validateDurationObject,
  validateFiatCurrencyObject,
  validatePhoneNumber,
} from "./utils";
import { AttachClaimTokens, EvaluatedOffersList, EvaluationResult, GetOffersRequest, OfferData, OfferTransactionStatusUpdateRequest, QueryProductsRequest, SimulationRequest } from "@credify/api-docs/apiClients/integration-v2";
import { MarketplaceApiFactory, ServiceProviderApiFactory } from "@credify/api-docs/apiClients/integration-v2";
import { Claims, OfferCondition } from "../types";
import { ConditionEvaluationResult, ConditionEvaluator } from "./evaluator";
import { Product } from "@credify/api-docs/apiClients/v2/model";
import { handleAPIConfiguration } from "./utils";

const ONBOARDING_STATUS = {
  pending: "PENDING",
  completed: "COMPLETED",
  failed: "FAILED",
};

export class Offer extends CredifyClass {
  /**
   * Gets a list of offers after filtering for a specific user.
   * @param request - GetOffersRequest.
   */
  public async getList(
    request: GetOffersRequest
  ): Promise<EvaluatedOffersList> {
    try {
      request.phone = validatePhoneNumber(request.phone)
      if (request?.credifyId) {
        request.credifyId = request.credifyId;
      }
      if (request?.productTypes) {
        request.productTypes = request.productTypes;
      }
      if (request?.bnpl) {
        request.bnpl = request.bnpl;
      }

      const configuration = handleAPIConfiguration(this.config.mode)
      const client = MarketplaceApiFactory(configuration, "", this.apiClient.instance as any)
      const res = await client.requestEvaluatedOfferList(request)
      if (!res.data.offers) {
        throw new Error("Unexpected object");
      }
      return res.data;
    } catch (e) {
      const  error = e;
      //console.log("request evaluated offer list error: ", error)
      throw e;
    }
  }

  /**
   * Updates a status of an offer transaction
   * @param id - ID of offer approval
   * @param request - OfferTransactionStatusUpdateRequest
   * @returns Promise<any>
   */
  public async updateStatus(
    id: string,
    request: OfferTransactionStatusUpdateRequest
  ): Promise<any> {
    if (!id) {
      throw new Error("ID should not be blank");
    }
    if (!isValidUUID(id)) {
      throw new Error("The order id must be in an UUID format");
    }

    if (!request.onboardingStatus) {
      throw new Error("onboarding status should not be blank")
    }

    const acceptableStatuses = Object.values(ONBOARDING_STATUS);
    if (!acceptableStatuses.includes(request.onboardingStatus)) {
      throw new Error(
        `The status needs to be one of ${acceptableStatuses.join(", ")}`
      );
    }

    if (request?.transactionAmount) {
      request.transactionAmount = validateFiatCurrencyObject(request.transactionAmount)
    }
    if (request?.vat) {
      request.vat = validateFiatCurrencyObject(request.vat)
    }

    try {
      const configuration = handleAPIConfiguration(this.config.mode)
      const client = ServiceProviderApiFactory(configuration, "", this.apiClient.instance as any)
      const res = await client.updateOnboardingStatus(id, request)
      return res
    } catch (error) {
      throw error;
    }
  }

  /**
   * Gets a detail of a specified offer
   * @param offerCode
   * @returns Offer detail info
   */
  public async getOfferDetail(offerCode: string): Promise<OfferData> {
    if (!offerCode) {
      throw new Error("Offer code should not be blank");
    }

    try {
      const configuration = handleAPIConfiguration(this.config.mode)
      const client = ServiceProviderApiFactory(configuration, "", this.apiClient.instance as any)
      const res = await client.getOfferDetail(offerCode)

      if (isObjectNullOrEmpty(res.data)) {
        throw new Error("Unexpected object");
      }
      return res.data
    } catch (error) {
      throw error;
    }
  }

  /**
   * Evaluate offer conditions based on scope definitions and user's claims.
   * @param conditions Offer conditions to evaluate
   * @param requiredCustomScopes Required custom scopes for each levels
   * @param userClaims Value of user claims to evaluate against
   * @returns evaluation result
   */
  public evaluateOffer(
    conditions: OfferCondition[] | any[] | null,
    requiredCustomScopes: string[][] | any[] | null,
    userClaims: Claims | null
  ): EvaluationResult {
    if (
      conditions == null ||
      requiredCustomScopes == null ||
      userClaims == null
    ) {
      return {
        rank: 0,
      };
    }

    let maxLevel = Math.max(conditions.length, requiredCustomScopes.length);
    if (maxLevel === 0) {
      return {
        rank: 0,
      };
    }

    let finalRank: number | null = null;
    let result = ConditionEvaluationResult.New();
    for (let i: number = 0; i < maxLevel; i++) {
      let conditionResult = ConditionEvaluationResult.New();
      conditionResult.rank = 1;
      if (i < conditions.length) {
        let condition = conditions[i];
        if (!isObjectNullOrEmpty(condition)) {
          if (typeof condition === "object") {
            condition = camelize(condition);
          }
          let evaluator = ConditionEvaluator.New(condition);
          conditionResult = evaluator.Evaluate(userClaims);
        }
      }

      let scopeResult = ConditionEvaluationResult.New();
      scopeResult.rank = 1;
      if (i < requiredCustomScopes.length) {
        let currentRequiredScopes = requiredCustomScopes[i];
        if (currentRequiredScopes != null) {
          scopeResult.requestedScopes = currentRequiredScopes;
          scopeResult.usedScopes = currentRequiredScopes;
          for (let requiredScope of currentRequiredScopes) {
            if (!userClaims.hasOwnProperty(requiredScope)) {
              scopeResult.rank = 0;
            }
          }
        }
      }

      result.requestedScopes = [
        ...result.requestedScopes!,
        ...conditionResult.requestedScopes!,
        ...scopeResult.requestedScopes!,
      ];

      if (finalRank != null) {
        //  failed at previous level, just continue
        continue;
      }

      if (conditionResult.rank! > 0 && scopeResult.rank! > 0) {
        //  level passed, add used scopes
        result.usedScopes = [
          ...result.usedScopes!,
          ...conditionResult.usedScopes!,
          ...scopeResult.usedScopes!,
        ];
      } else {
        //  set max rank as current level is failed now
        finalRank = i;
      }
    }
    //  if all passed, rank should be max level
    if (finalRank == null) {
      finalRank = maxLevel;
    }

    return {
      /** This user's level. Its min is 0, while its max is the number of `levels`. */
      rank: finalRank,
      requestedScopes: [...new Set(result.requestedScopes!)],
      usedScopes: [...new Set(result.usedScopes!)],
    };
  }

  /**
   * get simulation
   * @param productType - type of product
   * @param request - SimulationRequest
   * @returns Promise<any>
   */
  public async simulate(
    productType: string,
    request: SimulationRequest
  ): Promise<any> {
    if (request?.inputs?.transactionAmount) {
      request.inputs.transactionAmount = validateFiatCurrencyObject(request.inputs.transactionAmount)
    }
    if (request?.inputs?.loanTenor) {
      request.inputs.loanTenor = validateDurationObject(request.inputs.loanTenor)
    }
    if (request?.inputs?.downPayment) {
      request.inputs.downPayment = validateFiatCurrencyObject(request.inputs.downPayment)
    }

    try {
      const configuration = handleAPIConfiguration(this.config.mode)
      const client = MarketplaceApiFactory(configuration, "", this.apiClient.instance as any)
      const res = await client.simulateProductConfiguration(productType, request)
      if (!res.data) {
        throw new Error("Unexpected object");
      }
      return res.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * query product
   * @param request - QueryProductsRequest
   * @returns Promise<any>
   */
  public async queryProduct(
    request: QueryProductsRequest
  ): Promise<any> {
    request.phone = validatePhoneNumber(request.phone)
    if (request?.bnpl?.totalAmount) {
      request.bnpl.totalAmount = validateFiatCurrencyObject(request.bnpl.totalAmount)
    }

    try {
      const configuration = handleAPIConfiguration(this.config.mode)
      const client = MarketplaceApiFactory(configuration, "", this.apiClient.instance as any)
      const res = await client.queryProducts(request)

      return res.data
    } catch (error) {
      throw error;
    }
  }

  /**
   * update claims value
   * @param id - individual id (or credify id)
   * @param claimTokens - list of JWT token 
   * @returns Promise<any>
   */
  public async updateClaimsValue(
    id: string,
    claimTokens: Array<string>
  ): Promise<any> {
    if (!id) {
      throw new Error("Credify id must not be blank")
    }
    if (!isValidUUID(id)) {
      throw new Error("Credify id must be in an UUID format");
    }
    try {
      const configuration = handleAPIConfiguration(this.config.mode)
      const client = MarketplaceApiFactory(configuration, "", this.apiClient.instance as any)
      const attachedClaimToken = { claimTokens } as AttachClaimTokens
      const res = await client.updateClaimValues(id, attachedClaimToken)

      return res.data
    } catch(error) {
      throw error;
    }
  }

  /**
   * Query products
   * @param request - QueryProductsRequest
   * @returns Promise<object>
   */
  public async queryProducts(
    request: QueryProductsRequest
  ) {
    try {
      request.phone = validatePhoneNumber(request.phone)
      if (request?.bnpl?.totalAmount) {
        request.bnpl.totalAmount = validateFiatCurrencyObject(request.bnpl.totalAmount)
      }
      
      const configuration = handleAPIConfiguration(this.config.mode)
      const client = MarketplaceApiFactory(configuration, "", this.apiClient.instance as any)
      const res = await client.queryProducts(request)

      return res.data
    } catch (error) {
      console.log(error);
      throw error;
    }
  }
}
